import {defineStore, acceptHMRUpdate, storeToRefs} from 'pinia'
import axios from "axios";
import {useRegionStore} from "./region";
import {useRestapi} from "./restapi";
import {Notify, date} from "quasar";
import {ref, watch} from "vue";

export const usePatientStore = defineStore('patient', () => {
    const patient = ref(null);
    // Display-only AI summary for the currently loaded template patient. Kept
    // OUT of `patient` on purpose: everything on that object is a model field
    // that SavePatient posts back, and the summary must never be written (it is
    // generated from patient_description, so persisting it would make it its
    // own input). Separate ref = no save path can reach it.
    const ai_summary = ref(null);
    const flowsheets = ref(null);
    const viewing_visit = ref(null);
    const default_flowsheet = ref(null);
    const activity = ref(null);
    const selected_sim = ref(null);
    const students = ref([]);
    // The provider an instructor is documenting template content AS — the
    // person embed picked in the "Entering as" control — or null for
    // themselves, which is the default and by far the common case.
    //
    // Session state rather than patient data: it says who the AUTHOR is
    // currently speaking as, so it persists across the flowsheets of one
    // patient (an instructor populating a chart as Dr. Chen does not want to
    // re-pick per flowsheet) and means nothing once a different patient is
    // loaded.
    const entering_as = ref(null);

    // Reset on the patient itself changing, not on any one loader: `patient` is
    // assigned by GetPatient, SetPatient and SavePatient, and a reset written
    // into only some of them is a reset that leaks Dr. Chen into the next
    // patient's chart by whichever path was missed.
    watch(() => patient.value?.uid, (uid, previous_uid) => {
        if (uid !== previous_uid) entering_as.value = null;
    });

    axios.defaults.withCredentials = true

    function patient_time(now) {
        // Every caller either feeds this into datetime_to_epoch_offset (which
        // reads patient.epoch_datetime and would throw anyway, one frame deeper
        // in a helper shared by dozens of call sites) or compares it against
        // patient-epoch time (TimeList/TimeGrid `invalid_dates`). Returning raw
        // wall-clock `now` would therefore either relocate the crash to an
        // unattributable fingerprint or silently shade past/future rows wrong.
        // There is no correct value: name the bug and keep the precise frame.
        if (!patient.value) {
            console.error('patient_time called with no patient loaded — the caller is rendering or running against a torn-down patient');
            throw new Error('patient_time: no patient is loaded');
        }
        if (patient.value.activity && patient.value.activity.current_sim && patient.value.activity.current_sim.simulation_clock_offset !== null) {
            const seconds = patient.value.activity.current_sim.simulation_clock_offset * 60;
            if (patient.value.activity.current_sim.status === 'running' && patient.value.activity.current_sim.completed!==true) {
                return date.addToDate(now, { seconds: seconds });
            } else {
                return date.addToDate(patient.value.activity.current_sim.paused_datetime, { seconds: seconds });
            }
        }

        if (patient.value.patient_clock_offset) {
            return date.addToDate(now, { minutes: patient.value.patient_clock_offset });
        }
        return now;
    }

    async function api() {
        const regionStore = useRegionStore();
        return await regionStore.api();
    }

    // region.js's api() legitimately resolves to undefined when the current
    // user has no organization context (it redirects to /signup — see
    // region.apiOrgRace.test.js, issue #822). Every call site below used to
    // deref apiInstance.get/post/delete unguarded, so that edge case crashed
    // as an opaque "Cannot read properties of undefined" TypeError instead of
    // a clear, catchable failure (issue #904, Datadog 1bb1267e).
    function requireApi(apiInstance, action) {
        if (!apiInstance) {
            throw new Error(`Unable to ${action}: no organization context is available`);
        }
        return apiInstance;
    }

    // Companion to requireApi for the other legitimately-null piece of state:
    // `patient` is null before a chart's fetch lands and after a view is torn
    // down, so every function that addresses the API as `patient.value.uid`
    // could crash on it with an opaque "Cannot read properties of null"
    // TypeError. Name the failure instead — the same signal-preserving shape
    // requireApi and EndVisit already use (#901, #904).
    //
    // Callers below take the uid from the returned object BEFORE their first
    // await, so a navigation landing mid-request cannot retarget the URL (the
    // fix already applied to EndVisit for #808).
    function requirePatient(action) {
        if (!patient.value) {
            throw new Error(`Unable to ${action}: no patient is loaded`);
        }
        return patient.value;
    }

    async function GetFlowsheets(version, visit_type) {
        const apiInstance = requireApi(await api(), 'load flowsheets');
        const response = await apiInstance.get(`/patient_flowsheets/${version}/${visit_type}`);
        if (response.status === 200) {
            if (response.data.chart) {
                default_flowsheet.value = response.data.chart.default_flowsheet;
                flowsheets.value = response.data.chart.flowsheets;
                if (!default_flowsheet.value) {
                    default_flowsheet.value = flowsheets.value[0].uid;
                }
            } else {
                flowsheets.value = [];
            }
            return response.data;
        }
    }

    // Query params naming the activity a template patient is being viewed
    // through (#1162). GET /patient/{uid} admits a course member to a template
    // the library would hide from them (typically the instructor's Private
    // template) only when the request names the activity that uses it; the
    // backend verifies the activity really lists the template and that the
    // caller is in its course. Omitted entirely when there is no activity, so
    // requests outside an activity are byte-for-byte what they were.
    function templateContextParams(context) {
        if (!context || !context.activity_uid) return undefined;
        const params = { activity_uid: context.activity_uid };
        if (context.course_uid) params.course_uid = context.course_uid;
        return params;
    }

    // The activity context of the chart currently open, keyed by patient uid.
    // The chart's sub-resources (flowsheet data, visits) are gated exactly like
    // the patient itself, so every read of THIS patient's chart must carry the
    // same context GetPatient was given. Keyed by uid so a context can never
    // leak onto a different patient's requests.
    let request_context = null;

    function rememberRequestContext(uid, context) {
        const params = templateContextParams(context);
        if (params) {
            request_context = { patient_uid: uid, params };
        } else if (!request_context || request_context.patient_uid !== uid) {
            // A reload of the SAME chart from a route that dropped the query
            // (child routes such as Patient Details) keeps the context it was
            // opened with; any other patient starts without one.
            request_context = null;
        }
    }

    function contextParamsFor(patient_uid) {
        if (request_context && request_context.patient_uid === patient_uid) {
            return request_context.params;
        }
        return undefined;
    }

    async function GetPatientVisit(patient_uid, context) {
        // Scoped fetch used to check a patient's live current visit without
        // clobbering the `patient` ref, which other views treat as "the chart
        // currently being viewed". Unlike GetPatient(), this never assigns to it.
        const apiInstance = requireApi(await api(), 'load patient visit');
        const params = templateContextParams(context);
        const response = params
            ? await apiInstance.get(`/patient/${patient_uid}`, { params })
            : await apiInstance.get(`/patient/${patient_uid}`);
        if (response.status === 200) {
            return response.data.patient?.visit || null;
        }
    }

    async function GetPatientDataForFlowsheet(patient_uid, flowsheet_uid, version, backup_version, visit_uid) {
        const apiInstance = requireApi(await api(), 'load flowsheet data');
        let url = `/patient/${patient_uid}/flowsheet/${flowsheet_uid}`;
        if (viewing_visit.value || visit_uid) {
            url = `/patient/${patient_uid}/flowsheet/${flowsheet_uid}/visit/${viewing_visit.value?.uid || visit_uid}`;
        }
        const response = await apiInstance.get(url, {
            params: { version: version, backup_version: backup_version, ...contextParamsFor(patient_uid) }
        });
        if (response.status === 200) {
            return response.data;
        }
    }

    async function SaveData(data) {
        const patient_uid = requirePatient('save flowsheet data').uid;
        const apiInstance = requireApi(await api(), 'save flowsheet data');
        // Sent ONLY when a provider is chosen. "Myself" is the absence of the
        // key, not `entered_as: <my uid>` — the backend's no-`entered_as` path
        // is byte-identical to what it has always done, and a key that is
        // sometimes the session user would put an authorization check in front
        // of the ordinary save every student makes.
        const body = entering_as.value?.uid
            ? { ...data, entered_as: entering_as.value.uid }
            : data;
        const response = await apiInstance.post(`/patient/${patient_uid}/flowsheet/${data.flowsheet_uid}`, body);
        Notify.create({
            color: 'positive',
            message: `Saved Patient Data`,
            icon: 'save'
        });

        return response.data;
    }

    async function GetTemplatePatients(page, rowsPerPage, sortBy, descending, filter, filtering, semantic) {
        const apiInstance = requireApi(await api(), 'load template patients');
        const params = { query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending, filtering: filtering };
        // Only sent when the caller actually wants semantic mode. Omitted (not `false`)
        // otherwise, so the request never implies the mode exists for orgs without it.
        if (semantic === true) {
            params.semantic = true;
        }
        const response = await apiInstance.get(`/template_patients`, { params });
        if (response.status === 200) {
            flowsheets.value = response.data.flowsheets;
            return response.data;
        }
    }

    /**
     * Search providers visible to the session.
     *
     * DISCOVERY, and deliberately its own function. Resolving a charted
     * provider to a name must never go through here: a stored value has to keep
     * rendering even for a provider that is no longer offered — deleted,
     * unshared, or hidden by a future organization policy. Charted values carry
     * their own embed, so resolution needs no request at all. Keeping the two
     * apart is what stops a narrowing of discovery retroactively blanking
     * historical charts.
     */
    async function SearchProviders(query, filtering = 'all', rowsPerPage = 20) {
        const apiInstance = requireApi(await api(), 'search providers');
        const response = await apiInstance.get('/template_providers', {
            params: { query, filtering, rowsPerPage, page: 1 },
        });
        if (response.status === 200) {
            return response.data.providers;
        }
        return [];
    }

    async function SavePatient(data, update) {
        const apiInstance = requireApi(await api(), 'save patient');
        const response = await apiInstance.post(`/patient`, { patient: data });
        if (update !== false) {
            const previous_patient = patient.value;
            const previous_visit = viewing_visit.value;
            patient.value = response.data.patient;
            patient.value.activity = response.data.activity;
            // `patient.visit` is ONLY the patient's open visit — the backend
            // omits it once a visit has ended (Patient.get). This endpoint
            // saves the patient record and never opens, changes or ends a
            // visit; SaveVisit/EndVisit/DeleteVisit own that. So it may only
            // speak for the visit it actually reports on: the open one.
            //
            // Reassigning unconditionally is what closed a historical visit
            // out from under anyone who saved Patient Details while reading it
            // (#927), and would silently move a reader of an old visit onto the
            // current one. Update only when this save can be authoritative:
            // a different patient was loaded, nothing was being read, or the
            // visit being read IS the open visit — in which case a response
            // without one means it genuinely ended and must clear.
            const same_patient = !!previous_patient && previous_patient.uid === patient.value.uid;
            const was_reading_open_visit = !!previous_visit &&
                previous_visit.uid === previous_patient?.visit?.uid;
            if (!same_patient || !previous_visit || was_reading_open_visit) {
                viewing_visit.value = patient.value.visit || null;
            }
        }
        Notify.create({
            color: 'positive',
            message: `Saved Patient ${patient.value.name} successfully`,
            icon: 'save'
        });

        return response.data;
    }

    async function DeletePatient(patient) {
        const apiInstance = requireApi(await api(), 'delete patient');
        const response = await apiInstance.delete(`/patient/${patient.uid}`);
        Notify.create({
            color: 'info',
            message: `Patient ${patient.name} deleted successfully`,
            icon: 'delete'
        });
    }

    // The patient record as the server holds it now, for a view that shows
    // some patient other than the open chart (the Medication Cabinet header,
    // #1268). Never assigns `patient`, and throws on anything but a 200 so the
    // caller cannot mistake a failed read for an empty patient.
    async function FetchPatient(patient_uid, context) {
        const apiInstance = requireApi(await api(), 'load patient');
        const params = templateContextParams(context);
        const response = params
            ? await apiInstance.get(`/patient/${patient_uid}`, { params })
            : await apiInstance.get(`/patient/${patient_uid}`);
        if (response.status !== 200 || !response.data?.patient) {
            throw new Error(`Unable to load patient ${patient_uid}: HTTP ${response.status}`);
        }
        return response.data.patient;
    }

    function SetPatient(patientData) {
        patient.value = patientData;
        // Not fetched here, so any summary held is for a different patient.
        ai_summary.value = null;
        if (patient.value) {
            viewing_visit.value = patient.value.visit;
        } else {
            viewing_visit.value = null;
        }
    }

    async function GetPatient(uid, force, context) {
        // Recorded even when the fetch below short-circuits: the chart's
        // flowsheet and visit reads need this context either way.
        rememberRequestContext(uid, context);
        if (!patient.value || patient.value.uid !== uid || force === true) {
            const apiInstance = requireApi(await api(), 'load patient');
            // `context` = { course_uid, activity_uid } when the chart is opened
            // from an activity — see templateContextParams.
            const params = contextParamsFor(uid);
            const response = params
                ? await apiInstance.get(`/patient/${uid}`, { params })
                : await apiInstance.get(`/patient/${uid}`);
            if (response.status === 200) {
                patient.value = response.data.patient;
                // Top-level response field, not a patient field — see ai_summary above.
                ai_summary.value = response.data.ai_summary || null;
                patient.value.readonly = response.data.readonly;
                patient.value.activity = response.data.activity;
                patient.value.activity_student_patient = response.data.activity_student_patient;
                if (patient.value.visit) {
                    viewing_visit.value = patient.value.visit;
                } else {
                    viewing_visit.value = null;
                }
                return response.data;
            }
        }
    }

    async function LaunchPatient(activity_uid, course_uid, patientData) {
        if (patientData.patient_type === 'template') {
            // No instance exists yet - create one via API.
            //
            // `patient` is deliberately NOT cleared before this request. It used
            // to be, which opened a null window across the whole round trip
            // while the views that trigger a launch are still mounted — the
            // learning-module panel that owns chart links renders INSIDE
            // PatientLayout (StudentContentModule, under its `v-if="patient"`),
            // so a launch blanked the chart the student was reading for the
            // duration of the POST and drove every consumer against a null.
            //
            // This is NOT a demonstrated fix for Datadog 13ee6f0a / #901.
            // PatientLayout's clock teleport is itself gated on `patient`
            // (PatientLayout.vue:4-5, predating this change), so patient_time()
            // is never reached during this window and the clock cannot be the
            // producer of that crash. The real producer is still unidentified.
            // What this removes is an unnecessary null window, nothing more.
            //
            // Nothing is displayed from the new patient until it exists, so swap
            // once, here, when it does. A failed launch leaves the chart that was
            // already open on screen, which is the correct one to still be showing.
            const apiInstance = requireApi(await api(), 'launch patient');
            const response = await apiInstance.post(`/patient/instance/${patientData.uid}`,
              {activity_uid: activity_uid,
                course_uid: course_uid,
                timezone_offset: new Date().getTimezoneOffset()
              });
            if (response.status === 200) {
                patient.value = response.data.patient;
                // Belongs to whichever patient was loaded before — see ai_summary above.
                ai_summary.value = null;
                patient.value.activity = response.data.activity;
                viewing_visit.value = patient.value.visit;
                return `/patient/${response.data.patient.uid}`;
            }
        } else {
            // Instance already exists - use instance_patient_uid, NOT the template uid.
            // Nothing is fetched here, so the destination route has to load it,
            // and GetPatient() short-circuits when the uid already matches what
            // is loaded — clearing is what forces that fetch.
            //
            // Consumers DO observe this null before the caller navigates. This
            // function is async, so the caller's `.then` runs as a later
            // microtask, while this write queues Vue's flushJobs as an earlier
            // one — a pre-flush effect sees `patient === null` first. What makes
            // that harmless is the `v-if="patient"` render boundary every
            // consumer sits behind, NOT the ordering. And it is the common
            // student path, not an edge case: StudentActivity rewrites
            // patient_type to individual/shared once an instance exists (:405,
            // :500), so re-opening an already-launched chart lands here.
            patient.value = null;
            ai_summary.value = null;
            const patientUid = patientData.instance_patient_uid || patientData.uid;
            return `/patient/${patientUid}`;
        }
    }

    async function copy_patient(patient_uid) {
        // No pre-clear, for the same reason as LaunchPatient: the copy does not
        // exist until the response lands, and until then the currently loaded
        // patient is still the one on screen.
        //
        // Worth knowing before relying on that: the only production caller,
        // PatientTemplates.copyedit_patient (:461), calls SetPatient(null)
        // immediately before this, so in practice the store is null for the
        // whole copy regardless. This makes the store correct on its own terms;
        // it does not, by itself, change what that screen does.
        const apiInstance = requireApi(await api(), 'copy patient');
        const response = await apiInstance.post(`/patient/copy_patient/${patient_uid}`, {});
        if (response.status === 200) {
            patient.value = response.data.patient;
            ai_summary.value = null;
            viewing_visit.value = patient.value.visit;
            return response.data;
        }
    }

    async function SaveVisit(visit) {
        const patient_uid = requirePatient('save visit').uid;
        const apiInstance = requireApi(await api(), 'save visit');
        const response = await apiInstance.post(`/patient/${patient_uid}/visit`, { visit: visit });
        viewing_visit.value = response.data.visit;
        if (patient.value && ((!patient.value.visit && !viewing_visit.value.end_timestamp) || (patient.value.visit && patient.value.visit.uid === viewing_visit.value.uid))) {
            patient.value.visit = response.data.visit;
        }
        if (patient.value && patient.value.visit && patient.value.visit.uid === viewing_visit.value.uid && patient.value.visit.end_timestamp) {
            patient.value.visit = null;
        }
        Notify.create({
            color: 'positive',
            message: `Saved Visit successfully`,
            icon: 'save'
        });

        return response.data;
    }

    async function DeleteVisit(visit) {
        const patient_uid = requirePatient('delete visit').uid;
        const apiInstance = requireApi(await api(), 'delete visit');
        const response = await apiInstance.delete(`/patient/${patient_uid}/visit/${visit.uid}`);
        if (viewing_visit.value && viewing_visit.value.uid === visit.uid) {
            viewing_visit.value = null;
        }
        if (patient.value && patient.value.visit && patient.value.visit.uid === visit.uid) {
            patient.value.visit = null;
        }
        Notify.create({
            color: 'info',
            message: `Visit deleted successfully`,
            icon: 'delete'
        });
    }

    async function EndVisit(end_timestamp) {
        // Reaching here with no patient/visit means a stale End Visit dialog was
        // submitted after the visit went away underneath it — PatientLayout's
        // watch(viewing_visit) normally closes the dialog reactively first, so
        // this is only the residual race where the visit vanished in the same
        // tick as the submit. NEVER return silently: a no-op here looks exactly
        // like success while the visit stays open — instructor-visible data
        // loss with no telemetry. Tell the user, and keep the error in Datadog.
        //
        // This toast is a deliberate exception to the "global restapi.js handler
        // is the only toast" house rule (Owner's rule, 2026-09-29): that rule
        // covers *request* failures, and this branch returns before any request
        // is made (before the first `await api()`), so the global handler never
        // runs and never sees this case. Without this toast the student gets no
        // signal at all.
        if (!patient.value || !viewing_visit.value) {
            const message = 'This visit is no longer open, so it was not ended. Reload the chart and try again.';
            console.error('EndVisit called with no active patient/visit', {
                has_patient: !!patient.value,
                has_viewing_visit: !!viewing_visit.value
            });
            Notify.create({
                color: 'negative',
                message: message,
                icon: 'error'
            });
            throw new Error(message);
        }
        // Capture uids before the first await: a concurrent navigation can
        // clear patient/viewing_visit while the request is in flight.
        const patient_uid = patient.value.uid;
        const visit_uid = viewing_visit.value.uid;
        const apiInstance = requireApi(await api(), 'end visit');
        const response = await apiInstance.post(`/patient/${patient_uid}/visit/${visit_uid}/end`, { end_timestamp: end_timestamp });
        if (patient.value) {
            patient.value.visit = null;
        }
        viewing_visit.value = null;
        Notify.create({
            color: 'positive',
            message: `Visit Ended successfully`,
            icon: 'save'
        });

        return response.data;
    }

    async function GetVisit(visit_uid) {
        const patient_uid = requirePatient('load visit').uid;
        const apiInstance = requireApi(await api(), 'load visit');
        const params = contextParamsFor(patient_uid);
        const response = params
            ? await apiInstance.get(`/patient/${patient_uid}/visit/${visit_uid}`, { params })
            : await apiInstance.get(`/patient/${patient_uid}/visit/${visit_uid}`);
        return response.data;
    }

    async function GetVisits() {
        const patient_uid = requirePatient('load visits').uid;
        const apiInstance = requireApi(await api(), 'load visits');
        const params = contextParamsFor(patient_uid);
        const response = params
            ? await apiInstance.get(`/patient/${patient_uid}/visits`, { params })
            : await apiInstance.get(`/patient/${patient_uid}/visits`);
        return response.data;
    }

    async function SaveGrade(asp, options) {
        const apiInstance = requireApi(await api(), 'save grade');
        const response = await apiInstance.post(`/grade`, { asp: asp }, options);
        return response.data;
    }

    async function addScene(patient_uid, scene_uid) {
        const apiInstance = requireApi(await api(), 'add event');
        // `mayHaveCompleted: true` - a dropped response here doesn't mean the
        // event wasn't added; restapi.js's error_intercept uses the softer
        // "this may have been saved" wording instead of "nothing was saved".
        const response = await apiInstance.post(`/patient/${patient_uid}/add_scene/${scene_uid}`, {}, { mayHaveCompleted: true });
        Notify.create({
            color: 'positive',
            message: `Event added successfully`,
            icon: 'save'
        });
        return response.data;
    }

    async function GetDrugGuide(drug_name) {
        const apiInstance = requireApi(await api(), 'load drug guide');
        const response = await apiInstance.post(`/drugguide`, {drug_name: drug_name});
        return response.data;
    }

    return {
        patient,
        ai_summary,
        flowsheets,
        viewing_visit,
        default_flowsheet,
        activity,
        selected_sim,
        students,
        entering_as,
        patient_time,
        api,
        GetFlowsheets,
        GetPatientVisit,
        FetchPatient,
        GetPatientDataForFlowsheet,
        SaveData,
        GetTemplatePatients,
        SearchProviders,
        SavePatient,
        DeletePatient,
        SetPatient,
        GetPatient,
        LaunchPatient,
        copy_patient,
        SaveVisit,
        DeleteVisit,
        EndVisit,
        GetVisit,
        GetVisits,
        SaveGrade,
        addScene,
        GetDrugGuide
    };
});
